<i18n src="./resources/locales.json"></i18n>

<template>
  <div id="contacts_directory_container">
    <v-navigation-drawer
      v-model="opened"
      class="elevation-5 ecs-contacts-directory-container"
      :offline="isOfflineModeEnabled"
      app
      clipped
      fixed
      hide-overlay
      right
      stateless
      temporary
    >
      <EcsSearchInput
        ref="directorySearchInput"
        class="pa-4"
        :placeholder="$t('searchContactPlaceholder')"
        @search-input-changed="directorySearchChanged"
      />
      <v-expansion-panels v-model="panel" accordion active-class="surface lighten-1">
        <EcsContactsList
          :header="$t('sectionLabels.favorites')"
          :items="favorites"
          :search="searchText"
          :max-height="availableDirectoryHeight"
          :loading="loadingContacts || loadingCrmContacts"
          :drag-enabled="!searchText.length"
          show-contact-type
          @emit-error-message="setSnackbarMessage"
          @contacts-updated="updateFavoriteContactsFromDirectoryTab"
        />
        <EcsContactsList
          :header="$t('sectionLabels.supervisors')"
          :items="supervisorsToDisplay"
          :search="searchText"
          :max-height="availableDirectoryHeight"
          :loading="loadingContacts"
          @emit-error-message="setSnackbarMessage"
        />
        <EcsContactsList
          :header="$t('sectionLabels.agents')"
          :items="agentsToDisplay"
          :search="searchText"
          :max-height="availableDirectoryHeight"
          :loading="loadingContacts"
          @emit-error-message="setSnackbarMessage"
        />
        <EcsContactsList
          :header="$t('sectionLabels.channels')"
          :items="channelsToDisplay"
          :search="searchText"
          :max-height="availableDirectoryHeight"
          :loading="loadingContacts"
          @emit-error-message="setSnackbarMessage"
        />
        <EcsContactsList
          :header="$t('sectionLabels.bps')"
          :items="businessProcessesToDisplay"
          :search="searchText"
          :max-height="availableDirectoryHeight"
          :loading="loadingContacts"
          @emit-error-message="setSnackbarMessage"
        />
        <EcsContactsList
          :header="$t('sectionLabels.hpbx')"
          :items="enterpriseContactsToDisplay"
          :search="searchText"
          :loading="loadingContacts"
          :max-height="availableDirectoryHeight"
          @emit-error-message="setSnackbarMessage"
        />
        <EcsContactsList
          ref="crmContactsContainer"
          :header="$t('sectionLabels.crmContacts')"
          :items="crmContactsToDisplay"
          :search="searchText"
          :loading="loadingContacts || loadingCrmContacts"
          :max-height="availableDirectoryHeight"
          @emit-error-message="setSnackbarMessage"
          @load-more-data="onLoadingCrmContacts"
        />
        <EcsContactsList
          :header="$t('sectionLabels.addressBook')"
          :items="externalContactsToDisplay"
          :search="searchText"
          :max-height="availableDirectoryHeight"
          :loading="loadingContacts"
          @emit-error-message="setSnackbarMessage"
        />
      </v-expansion-panels>
    </v-navigation-drawer>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import EcsContactsList from '@/components/ecs-contacts-directory/EcsContactsList';
import { filterSupervisorsFromAgents, filterAgentById } from '@/utils';
import ContactService from '@/services/contactService';
import EcsCrmContact from '@/classes/contacts/EcsCrmContact';
import { CONTACT_TYPE } from '@/constants/contactConstants';

export default {
  name: 'EcsContactsDirectory',

  components: { EcsContactsList },

  data() {
    return {
      availableDirectoryHeight: 0,
      availableTotalHeight: 0,
      searchText: '',
      opened: false,
      panel: -1,
      panelTwo: -1,
      crm: {
        data: [],
        nextNumberOfMatches: 25
      },
      loadingContacts: false,
      loadingCrmContacts: false
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),

    ...mapGetters('CONTACTS', [
      'getBusinessProcesses',
      'getAgents',
      'getEnterpriseContacts',
      'getSupervisors',
      'getExternalContacts',
      'getCrmContacts',
      'getFavoriteContacts',
      'getChannels',
      'getAllContacts',
      'getContactV2'
    ]),

    ...mapGetters('INTERACTIONS', ['getCrmContactsForInteractions']),
    ...mapGetters('SETTINGS', ['isOfflineModeEnabled']),

    agents() {
      let filteredAgents = filterSupervisorsFromAgents(this.getAgents, this.getSupervisors);
      filteredAgents = filterAgentById(filteredAgents, this.loggedInAgent.id);
      return this.filterContacts(filteredAgents, this.searchText);
    },

    supervisors() {
      return this.filterContacts(this.getSupervisors, this.searchText);
    },

    channels() {
      return this.filterContacts(this.getChannels, this.searchText);
    },

    businessProcesses() {
      return this.filterContacts(this.getBusinessProcesses, this.searchText);
    },

    enterpriseContacts() {
      return this.filterContacts(this.getEnterpriseContacts, this.searchText);
    },

    externalContacts() {
      return this.filterContacts(this.getExternalContacts, this.searchText);
    },

    supervisorsToDisplay() {
      return this.panel === 1 ? this.supervisors : [];
    },

    agentsToDisplay() {
      return this.panel === 2 ? this.agents : [];
    },

    channelsToDisplay() {
      return this.panel === 3 ? this.channels : [];
    },

    businessProcessesToDisplay() {
      return this.panel === 4 ? this.businessProcesses : [];
    },

    enterpriseContactsToDisplay() {
      return this.panel === 5 ? this.enterpriseContacts : [];
    },

    crmContacts() {
      const filteredRecentUsedContacts = this.filterContacts(this.getCrmContactsForInteractions, this.searchText).sort(
        this.sortByFavorites
      );

      return [...filteredRecentUsedContacts, ...this.crm.data];
    },

    crmContactsToDisplay() {
      return this.crmContacts || [];
    },

    externalContactsToDisplay() {
      return this.externalContacts || [];
    },

    favorites() {
      if (this.panel !== 0) {
        return [];
      }

      let filteredFavorites = [];

      if (this.getFavoriteContacts.favorites && this.getFavoriteContacts.favorites.length) {
        filteredFavorites = this.getFavoriteContacts.favorites
          .map(({ id }) => this.getContactV2(id))
          .filter(contact => contact);
      } else {
        filteredFavorites = ([...this.getFavoriteContacts.internal, ...this.getFavoriteContacts.external] || [])
          .map(favoriteId => this.getContactV2(favoriteId))
          .filter(contact => contact);
      }

      return this.filterContacts(filteredFavorites, this.searchText);
    }
  },

  watch: {
    availableTotalHeight(height) {
      // 8 Categories (48px each header)
      this.availableDirectoryHeight = height - 48 * 8;
    },

    opened(isOpen) {
      if (isOpen) {
        window.addEventListener('resize', this.windowResizeListener);
      } else {
        window.removeEventListener('resize', this.windowResizeListener);
        this.$refs.directorySearchInput.clear();
      }
    },

    getFavoriteContacts() {
      if (!this.opened) {
        return;
      }

      this.requestMissingFavoritesCrmData();
    }
  },

  created() {
    this.$root.$on('request-contacts', this.requestContacts);
    this.requestContacts();
  },

  mounted() {
    const appHeaderHeight = this.isOfflineModeEnabled ? 94 : 64;
    this.availableTotalHeight = window.innerHeight - appHeaderHeight - 2 - 68;
  },

  beforeDestroy() {
    this.$root.$off('request-contacts', this.requestContacts);
  },

  methods: {
    ...mapActions('CONTACTS', [
      'requestCrmContact',
      'removeCrmContacts',
      'requestMainContacts',
      'requestCrmContactBatch',
      'requestFavoriteContacts',
      'updateFavoriteContacts',
      'requestCompanyContacts',
      'requestExternalContacts'
    ]),

    requestContacts() {
      if (this.loadingContacts) {
        return;
      }

      const contactPromises = [];
      if (
        this.getAgents.length === 0 ||
        this.getSupervisors.length === 0 ||
        this.getChannels.length === 0 ||
        this.getBusinessProcesses.length === 0
      ) {
        contactPromises.push(this.requestMainContacts());
      }

      if (this.getEnterpriseContacts.length === 0) {
        contactPromises.push(this.requestCompanyContacts());
      }

      if (this.getExternalContacts.length === 0) {
        contactPromises.push(this.requestExternalContacts());
        contactPromises.push(this.searchCrmContacts(this.searchText));
      }

      contactPromises.push(this.requestFavoriteContacts(this.loggedInAgent.id));

      this.loadingContacts = true;
      Promise.all(contactPromises)
        .then(this.requestMissingFavoritesCrmData)
        .finally(() => (this.loadingContacts = false));
    },

    directorySearchChanged(searchText) {
      this.searchText = searchText;
      this.$refs.crmContactsContainer.restartScroll();
      this.searchCrmContacts(searchText);
    },

    setSnackbarMessage(message) {
      this.$root.$emit('snackbar-message', message);
    },

    filterContacts(listToFilter, textToFilter) {
      if (!listToFilter) {
        return [];
      }

      let filteredList = listToFilter;

      if (textToFilter && textToFilter.trim() !== '') {
        filteredList = filteredList.filter(contact => {
          return (
            this.verifyByField(contact, 'name', textToFilter) ||
            this.verifyByField(contact, 'firstName', textToFilter) ||
            this.verifyByField(contact, 'lastName', textToFilter) ||
            this.verifyByField(contact, 'endPoint', textToFilter) ||
            this.verifyByField(contact, 'phones', textToFilter) ||
            this.verifyByField(contact, 'email', textToFilter) ||
            this.verifyByField(contact, 'emails', textToFilter) ||
            this.verifyByField(contact, 'HPBX Extension', textToFilter) ||
            this.verifyByField(contact, 'Mobile Phone', textToFilter) ||
            this.verifyByField(contact, 'Primary Email', textToFilter) ||
            this.verifyByField(contact, 'name', textToFilter) ||
            this.verifyByField(contact, 'endPoint', textToFilter) ||
            this.verifyByField(contact, 'searchString', textToFilter)
          );
        });
      }

      return filteredList;
    },

    verifyByField(contact, field, searchCriteria) {
      let containsCriteria = false;

      if (Array.isArray(contact[field])) {
        contact[field].forEach(objectValue => {
          if (typeof objectValue.value === 'string' && objectValue.value.toLowerCase().includes(searchCriteria)) {
            containsCriteria = true;
          }
        });
      } else {
        containsCriteria =
          typeof contact[field] === 'string' && contact[field].toLowerCase().includes(searchCriteria.toLowerCase());
      }

      return containsCriteria;
    },

    windowResizeListener() {
      const appHeaderHeight = this.isOfflineModeEnabled ? 94 : 64;
      this.availableTotalHeight = window.innerHeight - appHeaderHeight - 2 - 68;
    },

    updateFavoriteContactsFromDirectoryTab(contacts) {
      const favoriteContacts = this.getFavoriteContacts;
      favoriteContacts.favorites = contacts.map(contact => {
        return {
          id: contact.id,
          contactTypeId: contact.contactType
        };
      });
      favoriteContacts.internal = [];
      favoriteContacts.external = [];
      this.updateFavoriteContacts(favoriteContacts);
    },

    searchCrmContacts(searchText, page = 1) {
      if (page === 1) {
        this.crm = {
          data: [],
          nextNumberOfMatches: 25
        };
      }

      if (this.crm.nextNumberOfMatches === 0) {
        return;
      }

      let service;
      if (searchText) {
        service = ContactService.basicSearchCrmContact(searchText, this.crm.nextNumberOfMatches, page);
      } else {
        service = ContactService.advancedSearchCrmContact({}, this.crm.nextNumberOfMatches, page);
      }

      this.loadingCrmContacts = true;

      return service
        .then(response => {
          const newContacts = response.data.contacts.map(contact => {
            return new EcsCrmContact(contact);
          });

          const contacts = page === 1 ? newContacts : [...this.crm.data, ...newContacts];
          this.crm = {
            data: contacts,
            nextNumberOfMatches: response.data.totalNumberOfMatches > contacts.length ? this.crm.nextNumberOfMatches : 0
          };
        })
        .catch(() => {
          this.crm.data = [...this.crm.data];
        })
        .finally(() => {
          this.loadingCrmContacts = false;
        });
    },

    requestMissingFavoritesCrmData() {
      let missingCrmContacts = [];

      if (this.getFavoriteContacts.favorites && this.getFavoriteContacts.favorites.length) {
        missingCrmContacts = this.getFavoriteContacts.favorites
          .filter(({ id }) => !this.getContactV2(id))
          .filter(({ contactTypeId }) => contactTypeId === CONTACT_TYPE.crm)
          .map(({ id }) => id);
      } else {
        missingCrmContacts = (this.getFavoriteContacts.external || []).filter(id => !this.getContactV2(id));
      }

      if (missingCrmContacts.length) {
        this.loadingCrmContacts = true;
        this.requestCrmContactBatch(missingCrmContacts).finally(() => {
          this.loadingCrmContacts = false;
        });
      }
    },

    onLoadingCrmContacts() {
      const nextPage = Math.ceil(this.crm.data.length / (this.crm.nextNumberOfMatches || 1)) + 1;
      this.searchCrmContacts(this.searchText, nextPage);
    },

    sortByFavorites(contactA, contactB) {
      if (contactA.isFavorite && !contactB.isFavorite) {
        return -1;
      } else if (!contactA.isFavorite && contactB.isFavorite) {
        return 1;
      } else {
        return 0;
      }
    }
  }
};
</script>

<style scoped>
.container--fluid {
  padding-bottom: 0 !important;
}
.ecs-tab span {
  letter-spacing: 0.14em;
}

.ecs-contacts-directory-container {
  background: var(--v-surface-lighten1);
  border-style: solid;
  border-width: 1px;
  border-color: #0000001a;
  border-right: none;
  top: 64px !important;
  width: 350px !important;
  z-index: 119;
}

[offline].ecs-contacts-directory-container {
  top: 94px !important;
}

.v-expansion-panel {
  box-shadow: none;
}
</style>
